import { useEffect, useRef, useState } from 'react';
import type { PlayletProps } from './types';
import { hideSpotsFrom, type HideSpot } from './hideSpots';

const WIN_FINDS = 3;

export type HideSeekPlayletProps = PlayletProps & {
  rng?: () => number;
  meta?: { hideSpots?: HideSpot[] };
};

function spotCenter(spot: HideSpot) {
  return { x: spot.x + spot.w / 2, y: spot.y + spot.h / 2 };
}

function pickUnused(spots: HideSpot[], used: Set<string>, rng: () => number): HideSpot {
  const unused = spots.filter((s) => !used.has(s.id));
  const pool = unused.length > 0 ? unused : spots;
  const idx = Math.min(pool.length - 1, Math.floor(rng() * pool.length));
  return pool[idx]!;
}

export function HideSeekPlaylet({
  goTo,
  onWin,
  onQuit,
  rng = Math.random,
  meta,
}: HideSeekPlayletProps) {
  const spots = hideSpotsFrom(meta);
  const usedRef = useRef(new Set<string>());
  const hidingRef = useRef<HideSpot | null>(null);
  const busyRef = useRef(false);
  const quitRef = useRef(false);
  const wonRef = useRef(false);
  const findsRef = useRef(0);
  const [finds, setFinds] = useState(0);

  async function hideAtNext() {
    if (quitRef.current || wonRef.current) return;
    const spot = pickUnused(spots, usedRef.current, rng);
    hidingRef.current = spot;
    busyRef.current = true;
    try {
      await goTo(spotCenter(spot));
      if (quitRef.current) return;
    } finally {
      busyRef.current = false;
    }
  }

  const hideAtNextRef = useRef(hideAtNext);
  hideAtNextRef.current = hideAtNext;

  // Idempotent under <StrictMode>'s mount simulation (fix sweep 2026-09-19, the global StrictMode
  // sweep): the effect used to start a hide on each of its two dev-mode runs, so the pet was sent
  // to spot A and immediately to spot B, and the FIRST `goTo` a test (or a dev e2e) saw was not
  // the spot that would count. A ref survives the simulated remount, so the second run is a no-op;
  // a genuinely new playlet instance has a fresh ref and starts its own first hide.
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    void hideAtNextRef.current();
  }, []);

  function handleSpot(id: string) {
    if (busyRef.current || quitRef.current || wonRef.current) return;
    if (hidingRef.current?.id !== id) return;
    usedRef.current.add(id);
    findsRef.current += 1;
    setFinds(findsRef.current);
    if (findsRef.current >= WIN_FINDS) {
      wonRef.current = true;
      onWin();
      return;
    }
    void hideAtNext();
  }

  function handleQuit() {
    quitRef.current = true;
    onQuit();
  }

  return (
    <div className="habitat-playlet">
      <span className="chip habitat-playlet-progress">{finds}/{WIN_FINDS}</span>
      <button type="button" className="chip habitat-playlet-quit" onClick={handleQuit}>
        Quit
      </button>
      <p className="form-note habitat-playlet-hint">Watch where they hide, then tap the spot</p>
      {spots.map((spot) => (
        <button
          key={spot.id}
          type="button"
          className="habitat-playlet-spot"
          aria-label={spot.id}
          style={{
            left: `${spot.x}%`,
            top: `${spot.y}%`,
            width: `${spot.w}%`,
            height: `${spot.h}%`,
          }}
          onClick={() => handleSpot(spot.id)}
        />
      ))}
    </div>
  );
}
